import type { ContentItem } from "@/content/types";
import { fillYearsFromProfiles } from "@/content/staff-facts";
import { useStaffProfiles } from "@/lib/staff-profiles";
import { Portrait } from "@/components/site/Portrait";
import { sortByPortraitOrder, useStaffPortraits } from "@/lib/staff-portraits";


export type PersonImage = { image: string; alt: string; personKey?: string };

/** Delar upp "Namn – Roll" i namn och roll. Kräver mellanslag runt tankstrecket. */
function splitTitle(title: string) {
  const match = title.match(/^(.*?)\s+[–—-]\s+(.*)$/);
  if (match) return { name: match[1], role: match[2] };
  return { name: title, role: "" };
}


/** Kort för grundare och ledning. Bilder matchas i ordning mot listan. */
export function PeopleGrid({
  people,
  images = [],
}: {
  people: ContentItem[];
  images?: (PersonImage | undefined)[];
}) {
  const portraits = useStaffPortraits();
  const profiles = useStaffProfiles();
  const entries = sortByPortraitOrder(
    people
      .map((person, i) => ({ person, media: images[i] }))
      .filter(
        ({ media }) =>
          !(media?.personKey && (portraits[media.personKey]?.hidden || portraits[media.personKey]?.deletedAt)),
      ),
    (entry) => entry.media?.personKey,
    portraits,
  );
  if (entries.length === 0) return null;

  return (
    <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
      {entries.map(({ person, media }) => {
        const { name, role } = splitTitle(person.title);
        return (
          <article
            key={person.title}
            className="rounded-xl border border-border bg-card p-6 shadow-soft"
          >
            {media ? <Portrait src={media.image} alt={media.alt} {...(media.personKey ? { personKey: media.personKey } : {})} className="size-24" /> : null}


            <h3 className={media ? "mt-4 text-base font-semibold" : "text-base font-semibold"}>
              {name}
            </h3>
            {role ? <p className="mt-1 text-sm text-primary">{role}</p> : null}
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{fillYearsFromProfiles(person.text, profiles)}</p>
          </article>
        );
      })}
    </div>
  );
}
